import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';
import { createFilterOptions } from '@mui/material/useAutocomplete';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment from 'moment';
import { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import NRSelect from '../../../NRControlledSelect/NRSelect';
import NRJiraTeamSelect from '../../../../pages/NRSettings/NRIntegrations/NRJiraIntegration/NRJiraTeamSelect';
import { CREATE_JIRA_TICKET_LABELS, JIRA_RPC_OPTION_SOURCE } from './createJiraTicket.constants';
import { getJiraFieldControlType, getJiraFieldRpcOptionSource, resolveJiraFieldOptions } from './createJiraTicket.utils';

const filterLabelOptions = createFilterOptions({
  ignoreCase: true,
  matchFrom: 'any',
  trim: true
});

const JIRA_DATE_FORMAT = 'YYYY-MM-DD';
const JIRA_DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSZZ';

function toMomentValue(value) {
  if (!value) {
    return null;
  }
  const parsed = moment(value);
  return parsed.isValid() ? parsed : null;
}

function requiredRules(field) {
  if (!field?.required) {
    return undefined;
  }

  return {
    required: CREATE_JIRA_TICKET_LABELS.FIELD_REQUIRED,
    validate: value => {
      if (Array.isArray(value)) {
        return value.length > 0 || CREATE_JIRA_TICKET_LABELS.FIELD_REQUIRED;
      }
      if (typeof value === 'string') {
        return value.trim().length > 0 || CREATE_JIRA_TICKET_LABELS.FIELD_REQUIRED;
      }
      return value != null || CREATE_JIRA_TICKET_LABELS.FIELD_REQUIRED;
    }
  };
}

function normalizeFreeTagValues(nextValue = []) {
  return (Array.isArray(nextValue) ? nextValue : [])
    .map(value => (typeof value === 'string' ? value.trim() : String(value?.inputValue || value?.label || value || '').trim()))
    .filter(Boolean);
}

function NRDynamicJiraFieldInput({ field, control, disabled, error, rpcOptions }) {
  const theme = useTheme();
  const controlType = getJiraFieldControlType(field);
  const rpcSource = getJiraFieldRpcOptionSource(field);
  const options = useMemo(() => resolveJiraFieldOptions(field, rpcOptions), [field, rpcOptions]);
  const freeTagOptions = useMemo(() => options.map(option => option.label || option.value), [options]);
  const rules = requiredRules(field);
  const fieldSx = { width: '100%', minWidth: 0 };

  if (rpcSource === JIRA_RPC_OPTION_SOURCE.TEAMS) {
    return (
      <Controller
        name={field.key}
        control={control}
        rules={rules}
        render={({ field: { value, onChange } }) => (
          <NRJiraTeamSelect
            id={`jira-dynamic-${field.key}`}
            value={value ?? ''}
            onChange={onChange}
            teams={rpcOptions?.teams || []}
            disabled={disabled}
            required={Boolean(field?.required)}
            error={Boolean(error)}
            helperText={error?.message}
            sxProps={fieldSx}
          />
        )}
      />
    );
  }

  if (controlType === 'singleSelect') {
    return (
      <Controller
        name={field.key}
        control={control}
        rules={rules}
        render={({ field: { value, onChange } }) => (
          <NRSelect
            id={`jira-dynamic-${field.key}`}
            value={value ?? ''}
            onChange={onChange}
            options={options}
            disabled={disabled}
            error={Boolean(error)}
            helperText={error?.message}
            showEmptyValue
            emptyValueLabel={CREATE_JIRA_TICKET_LABELS.SELECT_VALUE}
            displayEmpty
            showIcon={false}
            color="secondary"
            sxProps={fieldSx}
          />
        )}
      />
    );
  }

  if (controlType === 'multiSelect') {
    return (
      <Controller
        name={field.key}
        control={control}
        rules={rules}
        render={({ field: { value, onChange } }) => (
          <NRSelect
            id={`jira-dynamic-${field.key}`}
            value={Array.isArray(value) ? value : []}
            onChange={onChange}
            options={options}
            disabled={disabled}
            error={Boolean(error)}
            helperText={error?.message}
            multiple
            showEmptyValue={false}
            displayEmpty
            showIcon={false}
            color="secondary"
            sxProps={fieldSx}
          />
        )}
      />
    );
  }

  if (controlType === 'freeTags') {
    return (
      <Controller
        name={field.key}
        control={control}
        rules={rules}
        render={({ field: { value, onChange, onBlur, ref } }) => {
          const selectedValues = Array.isArray(value) ? value : [];

          return (
            <Autocomplete
              multiple
              freeSolo
              selectOnFocus
              clearOnBlur
              handleHomeEndKeys
              filterSelectedOptions
              options={freeTagOptions}
              value={selectedValues}
              disabled={disabled}
              onBlur={onBlur}
              onChange={(_, nextValue) => onChange(normalizeFreeTagValues(nextValue))}
              filterOptions={(optionList, params) => {
                const filtered = filterLabelOptions(optionList, params);
                const inputValue = params.inputValue.trim();
                const alreadySelected = selectedValues.some(
                  selected => selected.toLowerCase() === inputValue.toLowerCase()
                );
                const alreadyListed = optionList.some(option => option.toLowerCase() === inputValue.toLowerCase());

                if (inputValue && !alreadySelected && !alreadyListed) {
                  filtered.push({
                    inputValue,
                    label: `Add "${inputValue}"`
                  });
                }

                return filtered;
              }}
              getOptionLabel={option => {
                if (typeof option === 'string') {
                  return option;
                }
                return option?.inputValue || option?.label || '';
              }}
              isOptionEqualToValue={(option, selected) => {
                const optionLabel = typeof option === 'string' ? option : option?.inputValue || option?.label;
                return optionLabel?.toLowerCase() === String(selected || '').toLowerCase();
              }}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                const label = typeof option === 'string' ? option : option?.label || option?.inputValue;
                return (
                  <li key={key} {...optionProps}>
                    {label}
                  </li>
                );
              }}
              renderTags={(tagValue, getTagProps) => (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    maxHeight: theme.spacing(11),
                    overflowY: 'auto',
                    width: '100%',
                    py: 0.25
                  }}
                >
                  {tagValue.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        size="small"
                        color="secondary"
                        variant="outlined"
                        label={option}
                        {...tagProps}
                      />
                    );
                  })}
                </Box>
              )}
              renderInput={params => (
                <TextField
                  {...params}
                  inputRef={ref}
                  color="secondary"
                  error={Boolean(error)}
                  helperText={error?.message}
                  placeholder={selectedValues.length ? undefined : CREATE_JIRA_TICKET_LABELS.ENTER_VALUE}
                />
              )}
              componentsProps={{
                popper: {
                  sx: {
                    boxShadow: theme.shadows[3],
                    borderRadius: 1.5
                  }
                }
              }}
              sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': {
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'flex-start',
                  maxHeight: theme.spacing(15),
                  overflow: 'hidden',
                  py: 0.5
                },
                '& .MuiAutocomplete-input': {
                  minWidth: `${theme.spacing(10)} !important`,
                  width: 0,
                  flexGrow: 1
                }
              }}
            />
          );
        }}
      />
    );
  }

  if (controlType === 'date' || controlType === 'datetime') {
    const PickerComponent = controlType === 'datetime' ? DateTimePicker : DatePicker;
    const valueFormat = controlType === 'datetime' ? JIRA_DATETIME_FORMAT : JIRA_DATE_FORMAT;

    return (
      <Controller
        name={field.key}
        control={control}
        rules={rules}
        render={({ field: { value, onChange, onBlur, ref } }) => (
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <PickerComponent
              value={toMomentValue(value)}
              onChange={nextValue => onChange(nextValue?.isValid?.() ? nextValue.format(valueFormat) : '')}
              disabled={disabled}
              slotProps={{
                textField: {
                  id: `jira-dynamic-${field.key}`,
                  inputRef: ref,
                  onBlur,
                  color: 'secondary',
                  fullWidth: true,
                  error: Boolean(error),
                  helperText: error?.message,
                  placeholder: CREATE_JIRA_TICKET_LABELS.ENTER_VALUE
                }
              }}
            />
          </LocalizationProvider>
        )}
      />
    );
  }

  const inputType = controlType === 'number' ? 'number' : 'text';

  return (
    <Controller
      name={field.key}
      control={control}
      rules={rules}
      render={({ field: { value, onChange, onBlur, ref } }) => (
        <TextField
          id={`jira-dynamic-${field.key}`}
          inputRef={ref}
          value={value ?? ''}
          onChange={event => onChange(event.target.value)}
          onBlur={onBlur}
          disabled={disabled}
          error={Boolean(error)}
          helperText={error?.message}
          type={inputType}
          fullWidth
          color="secondary"
          placeholder={CREATE_JIRA_TICKET_LABELS.ENTER_VALUE}
        />
      )}
    />
  );
}

export default NRDynamicJiraFieldInput;
